import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

// Generates initials-based "logo" with brand accent color
function BookmakerMark({ name, color = "#00E676" }) {
  const initials = name
    .replace(/[^A-Za-z ]/g, "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="w-16 h-16 rounded-md flex items-center justify-center shrink-0 font-display font-black"
      style={{
        background: "var(--sb-bg)",
        border: `1px solid ${color}40`,
        color,
        fontSize: "20px",
        letterSpacing: "-0.04em",
      }}
    >
      {initials || "??"}
    </div>
  );
}

export default function OfferWall({ offers, compact = false }) {
  if (!offers?.length) return null;

  const onClick = async (e, offer) => {
    e.preventDefault();
    try {
      const res = await api.post(`/track/offer/${offer.id}`);
      window.open(res.data.url, "_blank", "noopener");
    } catch {
      window.open(offer.tracking_url, "_blank", "noopener");
    }
  };

  if (compact) {
    return (
      <section className="mt-8" data-testid="offerwall-compact">
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--sb-text-muted)" }}>
            // UK Bookmaker Offers
          </h2>
          <span className="font-mono text-[10px]" style={{ color: "var(--sb-text-muted)" }}>
            18+ T&Cs
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {offers.map((o) => (
            <a
              key={o.id}
              href={o.tracking_url}
              onClick={(e) => onClick(e, o)}
              data-testid={`offer-compact-${o.bookmaker.toLowerCase().replace(/\s+/g, "-")}`}
              className="sb-card rounded-lg px-3 py-2 flex items-center gap-2.5"
            >
              <span
                className="shrink-0 font-display font-black"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  border: `1px solid ${(o.accent_color || "#3D7EFF")}40`,
                  color: o.accent_color || "#3D7EFF",
                  background: "var(--sb-bg)",
                  fontSize: "12px",
                  letterSpacing: "-0.04em",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {o.bookmaker.replace(/[^A-Za-z ]/g, "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "??"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[9px] uppercase tracking-wider leading-tight" style={{ color: "var(--sb-text-muted)" }}>
                  {o.bookmaker}
                </p>
                <p className="font-mono text-[11px] font-semibold truncate leading-tight" style={{ color: "var(--sb-text)" }}>
                  {o.title}
                </p>
              </div>
              <span
                className="shrink-0 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-md"
                style={{ background: "var(--sb-primary)", color: "#000" }}
              >
                Claim <ArrowUpRight size={9} />
              </span>
            </a>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--sb-text-muted)" }}>
          // UK Bookmaker Offers
        </h2>
        <span className="font-mono text-[10px]" style={{ color: "var(--sb-text-muted)" }}>
          18+ T&Cs APPLY
        </span>
      </div>

      <div className="flex flex-col gap-3 sb-stagger">
        {offers.map((o) => (
          <article
            key={o.id}
            data-testid={`offer-card-${o.bookmaker.toLowerCase().replace(/\s+/g, "-")}`}
            className="sb-card rounded-lg p-4 sm:p-5"
          >
            <div className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-4 items-center">
              <BookmakerMark name={o.bookmaker} color={o.accent_color || "#00E676"} />

              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--sb-text-muted)" }}>
                    {o.bookmaker}
                  </span>
                  {o.badge ? (
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{
                        background: "rgba(61, 126, 255, 0.12)",
                        color: "var(--sb-primary)",
                        border: "1px solid rgba(61, 126, 255, 0.3)",
                      }}
                    >
                      {o.badge}
                    </span>
                  ) : null}
                </div>
                <h3 className="font-display text-lg md:text-xl font-bold leading-tight">{o.title}</h3>
                {o.description ? (
                  <p className="text-xs mt-1" style={{ color: "var(--sb-text-secondary)" }}>
                    {o.description}
                  </p>
                ) : null}
              </div>

              <div className="col-span-2 md:col-span-1 mt-1 md:mt-0 md:justify-self-end w-full md:w-auto">
                <a
                  href={o.tracking_url}
                  onClick={(e) => onClick(e, o)}
                  data-testid={`claim-offer-${o.bookmaker.toLowerCase().replace(/\s+/g, "-")}`}
                  className="sb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm uppercase tracking-wider w-full md:w-auto"
                >
                  Claim Offer <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
            <p className="text-[10px] mt-3 font-mono" style={{ color: "var(--sb-text-muted)" }}>
              {o.terms}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
